import ReactDOM from 'react-dom/client';
import { InitProjectsPageOptions, StorageKeys } from 'shared/interfaces/InitProjectsPageOptions.ts';
import { AppProviders } from 'src/AppProviders.tsx';
import { EmbeddedDatabaseProvider } from 'src/database/EmbeddedDatabaseProvider.tsx';
import { prepare } from 'src/prepareApp.tsx';
import { ProjectPage } from 'src/projects/ProjectPage.tsx';
import { StandaloneProjectNavigationProvider } from 'src/projects/navigation/StandaloneProjectNavigationProvider.tsx';
import { UserSettings } from 'src/types/UserSettings.ts';
import { Messages } from 'src/bootstrap/bootstrap.ts';
import { initSentry } from 'src/sentry.ts';
import { createOfflineDatabase } from 'src/database/createOfflineDatabase.ts';
import { UserLocalOptionsClientInterface } from 'src/common/UserLocalOptionsClient/UserLocalOptionsClientInterface.ts';
import { createUserLocalOptionsClient } from 'src/common/UserLocalOptionsClient/createUserLocalOptionsClient.ts';

/**
 * Routerless entry point that renders only the projects page, meant to be embedded inside a
 * host application that owns both routing and the database connection.
 *
 * - No react-router: the pagination page comes from `currentPage` and every navigation intent
 *   (page change, close, add-new-selection) is delegated back to the host through the
 *   `onPageChange` / `onGoBack` / `onGoHome` callbacks.
 * - No offline database bootstrap: the GraphQL/database layer is built on top of the
 *   `dbClient` handed in by the host.
 * - Storage key names and the default project prefix are configurable via `storageKeys` /
 *   `projectName`.
 */
export async function initProjectsPage({
    dbClient,
    currentPage,
    onNavigate,
    onPageChange,
    onGoBack,
    onGoHome,
    ...options
}: InitProjectsPageOptions & { userSettings: UserSettings; messages: Messages }) {
    const { shadowRoot, base } = await prepare({
        ...options,
        bootstrapFn: async (
            sentryHost: string,
            _: string,
            global: Window = window,
            storageKeys: Partial<StorageKeys> = {},
        ):Promise<{
            userSettings: UserSettings;
            messages: Messages;
            databaseCache: LocalForage;
            offlineDatabase: LocalForage;
            projectsStore: LocalForage;
            userOptionsClient: UserLocalOptionsClientInterface;
        }> => {
            initSentry(sentryHost, global.location.hostname);
            const {
                databaseCache,
                offlineDatabase,
                projectsStore,
                userOptionsStore,
            } = createOfflineDatabase(storageKeys);
            const userOptionsClient = await createUserLocalOptionsClient(userOptionsStore);
            await projectsStore.ready();
            return {
                userSettings: options.userSettings,
                messages: options.messages,
                databaseCache,
                offlineDatabase,
                projectsStore,
                userOptionsClient,
            };
        },
    });

    ReactDOM.createRoot(shadowRoot).render(
        <AppProviders
            {...base}
            renderDatabase={(children) => (
                <EmbeddedDatabaseProvider
                    dbClient={dbClient}
                    userSettings={base.userSettings}
                    uri={base.uri}
                >
                    {children}
                </EmbeddedDatabaseProvider>
            )}
        >
            <StandaloneProjectNavigationProvider
                initialPage={currentPage}
                onPageChange={onPageChange}
                onGoBack={onGoBack}
                onGoHome={onGoHome}
                onNavigate={onNavigate}
            >
                <ProjectPage/>
            </StandaloneProjectNavigationProvider>
        </AppProviders>,
    );
}
